<template>
  <div
    v-if="detail.GameName"
    class="game-detail"
    :class="{ 'game-detail-dark': isDarkMode }"
  >
    <IngaPortrait
      v-if="rewardImage"
      class="reward-photo mb-3"
      frame="reward"
      :src="rewardImage"
      :alt="rewardAlt"
      :sources="rewardSources"
      rotate
      nav
    />

    <div v-if="renderedGameText" class="game-text mb-3" v-html="renderedGameText"></div>

    <TabView class="moniex-tabs-forced">
      <TabPanel header="Hra">
        <div class="meta-box">
          <div class="row g-0">
            <div v-if="fromLabel || toLabel" class="col-6 px-3 py-2 meta-cell">
              <span class="meta-label">Trvání</span>
              <span class="meta-value">{{ fromLabel }} – {{ toLabel }}</span>
            </div>
            <div v-if="registerLabel" class="col-6 px-3 py-2 meta-cell">
              <span class="meta-label">Registrace</span>
              <span class="meta-value">{{ registerLabel }}</span>
            </div>
            <div v-if="listGame.GameCapital" class="col-6 px-3 py-2 meta-cell">
              <span class="meta-label">Kapitál</span>
              <span class="meta-value meta-capital">{{ formatNumber(listGame.GameCapital) }}</span>
            </div>
            <div v-if="listGame.GameCredit" class="col-6 px-3 py-2 meta-cell">
              <span class="meta-label">Vklad</span>
              <span class="meta-value">{{ formatNumber(listGame.GameCredit) }}</span>
            </div>
          </div>
        </div>
      </TabPanel>

      <TabPanel header="Výhry">
        <rewards-table :rows="rewards" />
      </TabPanel>

      <TabPanel header="Hráči">
        <vue-good-table
          styleClass="vgt-table condensed forced-vgt-table"
          :columns="playersTableColumns"
          :rows="players"
          :row-click="true"
          @row-click="onRowClick"
          :sort-options="{ enabled: false }"
          max-height="280px"
        >
          <template #table-row="props">
            <div v-if="props.column.field === 'UserNickName'" class="d-flex align-items-center py-1">
              <img
                :src="userAvatarUrl(props.row.UserImage, store.state.imageCacheKey, true)"
                class="player-avatar-circle"
                :class="{ 'player-avatar-dark': isDarkMode }"
                alt="player"
              >
              <button
                type="button"
                class="user-info-btn"
                :title="'Profil hráče ' + props.row.UserNickName"
                @click.stop.prevent="goToUser(props.row)"
              >i</button>
              <h6 class="mb-0 text-sm player-nick">
                {{ props.row.UserNickName }}
                <span v-if="isLoggedIn && Number(userID) === Number(props.row.UserID)" class="you-badge">Ty</span>
              </h6>
            </div>
            <span v-else-if="props.column.field === 'Score'" class="player-score-text">
              {{ props.row.HistoryGame }}/{{ props.row.OpenGame }}/{{ props.row.RunningGame }}
            </span>
            <span v-else>{{ props.formattedRow[props.column.field] }}</span>
          </template>
          <template #emptystate>
            <div class="text-center py-4 empty-players">
              <span class="d-block fw-medium">V místnosti zatím nikdo nesedí.</span>
              <span class="text-xs">Buď první, kdo se do hry zapojí.</span>
            </div>
          </template>
        </vue-good-table>
      </TabPanel>

      <TabPanel v-if="detail.RewardDetail" header="O výhře">
        <div class="tab-text-content" v-html="formatRichText(detail.RewardDetail.RewardText)"></div>
      </TabPanel>

      <TabPanel v-if="detail.Sponsor" header="Sponzor">
        <div class="tab-text-content">
          <div v-if="detail.Sponsor.SponsorURL" class="sponsor-link-box">
            <i class="bi bi-link-45deg"></i>
            <a :href="detail.Sponsor.SponsorURL" target="_blank" rel="noopener noreferrer">
              {{ detail.Sponsor.SponsorURL }}
            </a>
          </div>
          <div v-html="formatRichText(detail.Sponsor.SponsorText)"></div>
        </div>
      </TabPanel>
    </TabView>

    <div class="d-flex justify-content-between align-items-center pt-3 gap-2">
      <button
        v-if="detail.GameID"
        type="button"
        class="forum-open-btn"
        :title="'Fórum: ' + (detail.GameName || 'hra')"
        @click.stop="openGameForum"
      >
        <fa-icon icon="fa-regular fa-comment-dots" />
        Fórum
      </button>
      <span v-else></span>
      <span v-if="isAlreadyRegistered" class="badge rounded-pill px-3 py-2 fw-bold text-uppercase badge-joined">
        Jsi ve hře
      </span>
      <span
        v-else-if="registerOpen"
        class="badge rounded-pill px-3 py-2 fw-bold text-uppercase enter-btn"
        role="button"
        @click="addToGameHandler"
      >
        Vstoupit do hry
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import RewardsTable from "./tables/RewardsTable.vue";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import IngaPortrait from "./IngaPortrait.vue";
import { formatRichText } from "@/helpers/richText";
import { userAvatarUrl } from "@/helpers/userAvatars";
import { isRegisterActiveGlobal } from "@/utils/DateFunctions.js";
import { rewardImageFiles } from "@/helpers/rewardGallery";
import moment from "moment";

const store = useStore();
const router = useRouter();
const isDarkMode = computed(() => !!store.state.isDarkMode);

const detail = computed(() => {
  const raw = store.state.GameDetail;
  return raw && !Array.isArray(raw) ? raw : {};
});

const listGame = computed(() => {
  const games = store.state.OpenGame || [];
  return games.find((game) => Number(game.GameID) === Number(detail.value.GameID)) || {};
});

const imgRoot = computed(() => process.env.VUE_APP_IMG_ROOT);
const apiRoot = computed(() => process.env.VUE_APP_API_URL);
const userID = computed(() => store.getters.getUser?.UserID);
const isLoggedIn = computed(() => !!store.getters.isLoggedIn);

const resolveImage = (file) => {
  if (!file) return "";
  const value = String(file);
  if (value.startsWith("http")) return value;
  if (value.startsWith("img/")) return apiRoot.value + value;
  return imgRoot.value + value;
};

const rewardImage = computed(() => {
  return resolveImage(
    detail.value.RewardImg
    || detail.value.RewardDetail?.RewardImg
    || detail.value.Reward?.[0]?.RewardImg
    || listGame.value.GameRewardImg
  );
});

const rewardSources = computed(() => {
  const files = rewardImageFiles(
    detail.value.RewardImg
      || detail.value.RewardDetail?.RewardImg
      || detail.value.Reward?.[0]?.RewardImg
      || listGame.value.GameRewardImg,
    detail.value.RewardImgs
      || detail.value.RewardDetail?.RewardImgs
      || detail.value.Reward?.[0]?.RewardImgs
  );
  return files.map((file) => resolveImage(file)).filter(Boolean);
});

const rewardAlt = computed(() => {
  return detail.value.RewardDetail?.RewardName
    || listGame.value.GameReward
    || detail.value.GameName
    || "Výhra";
});

const renderedGameText = computed(() => formatRichText(listGame.value.GameText || ""));

const parseDate = (value) => moment(String(value || "").trim(), ["DD.MM.YYYY HH:mm:ss", "DD.MM.YYYY"]);
const fromLabel = computed(() => {
  const date = parseDate(listGame.value.GameFrom);
  return date.isValid() ? date.format("D. M. YYYY") : "";
});
const toLabel = computed(() => {
  const date = parseDate(listGame.value.GameTo);
  return date.isValid() ? date.format("D. M. YYYY") : "";
});
const registerLabel = computed(() => {
  const date = parseDate(listGame.value.GameRegister);
  return date.isValid() ? date.format("D. M. YYYY") : "";
});

const registerOpen = computed(() => isRegisterActiveGlobal(listGame.value.GameRegister, moment()));

const players = computed(() => detail.value.User || []);
const isAlreadyRegistered = computed(() => {
  if (!userID.value) return false;
  return players.value.some((player) => Number(player.UserID) === Number(userID.value));
});

const rewards = computed(() => (detail.value.Reward || []).map((row) => ({
  ...row,
  RewardOrder: row.RewardOrder ?? row.Order
})));

const formatNumber = (number) => store.getters.formatNumber(number);

const playersTableColumns = [
  {
    label: "Hráč",
    field: "UserNickName",
    thClass: "custom-th-style text-start",
    tdClass: "custom-td-style text-start",
  },
  {
    label: "Skóre",
    field: "Score",
    thClass: "custom-th-style text-end",
    tdClass: "custom-td-style text-end",
  },
];

const goToUser = (row) => {
  if (!row?.UserID) return;
  store.dispatch("closeModal");
  router.push({ path: `/hrac/${row.UserID}` });
};

const onRowClick = (params) => goToUser(params.row);

const addToGameHandler = () => {
  if (isAlreadyRegistered.value || !detail.value.GameID) return;
  store.dispatch("addToGameHandler", {
    GameID: detail.value.GameID,
    GameName: detail.value.GameName,
    GameCredit: listGame.value.GameCredit
  });
};

const openGameForum = () => {
  if (!detail.value.GameID) return;
  store.dispatch("openForum", {
    title: detail.value.GameName || "Hra",
    GameID: detail.value.GameID,
    Img: detail.value.RewardImg || listGame.value.GameRewardImg
  });
};
</script>

<style scoped>
.game-detail {
  padding: 0.25rem 0.75rem 0.5rem;
}

.game-detail-dark {
  color: #E2E8F0;
}

.reward-photo {
  width: 100%;
  display: block;
}

.game-text {
  font-size: 0.9rem;
  line-height: 1.55;
  color: #64748B;
  text-align: justify;
  hyphens: auto;
}

.game-text :deep(strong),
.game-text :deep(b) {
  font-weight: 800;
  color: #FF6B21;
}

.game-detail-dark .game-text {
  color: #CBD5E1;
}

.game-detail-dark .game-text :deep(strong),
.game-detail-dark .game-text :deep(b) {
  color: #FF8A4C;
}

.meta-box {
  border-radius: 0.85rem;
  overflow: hidden;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
}

.game-detail-dark .meta-box {
  background: #1E293B;
  border-color: #334155;
}

.meta-cell {
  border-bottom: 1px solid #E2E8F0;
}

.game-detail-dark .meta-cell {
  border-color: #334155;
}

.meta-label {
  display: block;
  text-transform: uppercase;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #64748B;
}

.game-detail-dark .meta-label {
  color: #94A3B8;
}

.meta-value {
  display: block;
  margin-top: 0.2rem;
  font-size: 0.88rem;
  font-weight: 800;
  color: #0F172A;
}

.game-detail-dark .meta-value {
  color: #E2E8F0;
}

.meta-capital {
  color: #059669;
}

.game-detail-dark .meta-capital {
  color: #34D399;
}

:deep(.moniex-tabs-forced .p-tabview-nav) {
  display: flex !important;
  gap: 6px !important;
  background-color: #F1F5F9 !important;
  padding: 5px !important;
  border-radius: 12px !important;
  border: none !important;
  list-style-type: none !important;
  margin-bottom: 1rem !important;
}

:deep(.moniex-tabs-forced .p-tabview-nav li) {
  flex: 1 !important;
}

:deep(.moniex-tabs-forced .p-tabview-nav-link) {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: transparent !important;
  color: #475569 !important;
  border: none !important;
  padding: 10px 12px !important;
  border-radius: 9px !important;
  font-weight: 700 !important;
  font-size: 0.85rem !important;
  text-decoration: none !important;
  box-shadow: none !important;
}

:deep(.moniex-tabs-forced .p-tabview-nav li.p-highlight .p-tabview-nav-link) {
  background-color: #ffffff !important;
  color: #475569 !important;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06) !important;
}

:deep(.moniex-tabs-forced .p-tabview-panels) {
  padding: 0 !important;
  background: transparent !important;
}

.game-detail-dark :deep(.moniex-tabs-forced .p-tabview-nav) {
  background-color: #1E293B !important;
}

.game-detail-dark :deep(.moniex-tabs-forced .p-tabview-nav-link) {
  color: #94A3B8 !important;
}

.game-detail-dark :deep(.moniex-tabs-forced .p-tabview-nav li.p-highlight .p-tabview-nav-link) {
  background-color: #475569 !important;
  color: #E2E8F0 !important;
  box-shadow: none !important;
}

:deep(.forced-vgt-table),
.game-detail-dark :deep(.vgt-table),
.game-detail-dark :deep(.vgt-wrap),
.game-detail-dark :deep(.vgt-responsive) {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

:deep(.custom-th-style) {
  background-color: #F8FAFC !important;
  border-bottom: 2px solid #E2E8F0 !important;
  padding: 10px 12px !important;
  color: #475569 !important;
  font-weight: 700 !important;
  font-size: 0.75rem !important;
  text-transform: uppercase !important;
}

:deep(.custom-td-style) {
  padding: 10px 12px !important;
  border-bottom: 1px solid #F1F5F9 !important;
  vertical-align: middle !important;
}

.game-detail-dark :deep(.custom-th-style),
.game-detail-dark :deep(.vgt-table th) {
  background: #1E293B !important;
  color: #94A3B8 !important;
  border-color: #334155 !important;
}

.game-detail-dark :deep(.custom-td-style),
.game-detail-dark :deep(.vgt-table td) {
  background: transparent !important;
  color: #E2E8F0 !important;
  border-color: #334155 !important;
}

.game-detail-dark :deep(.vgt-table tbody tr:hover) {
  background-color: #1E293B !important;
}

.game-detail-dark :deep(.text-dark) {
  color: #E2E8F0 !important;
}

.player-avatar-circle {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 8px;
  border: 1px solid #E2E8F0;
}

.player-avatar-dark {
  border-color: #334155;
}

.player-nick {
  color: #334155;
  font-weight: 700;
}

.game-detail-dark .player-nick {
  color: #E2E8F0;
}

.player-score-text {
  font-weight: 700;
  color: #0F172A;
}

.game-detail-dark .player-score-text {
  color: #E2E8F0;
}

.you-badge {
  background-color: #F1F5F9;
  color: #475569;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 20px;
  margin-left: 6px;
}

.game-detail-dark .you-badge {
  background-color: #1E293B;
  color: #FF8A4C;
}

.user-info-btn {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  margin-right: 8px;
  padding: 0;
  border-radius: 50%;
  border: 1.5px solid #94A3B8;
  background: transparent;
  color: #94A3B8;
  font-size: 0.68rem;
  font-weight: 800;
  font-style: italic;
  font-family: Georgia, "Times New Roman", serif;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.user-info-btn:hover {
  border-color: #FF8A4C;
  color: #FF8A4C;
}

.empty-players {
  color: #64748B;
}

.game-detail-dark .empty-players {
  color: #94A3B8;
}

.tab-text-content {
  text-align: left;
  padding: 0.5rem 0.25rem 0.25rem;
  font-size: 0.95rem;
  line-height: 1.6;
}

.tab-text-content :deep(strong),
.tab-text-content :deep(b) {
  font-weight: 800;
  color: #FF6B21;
}

.game-detail-dark .tab-text-content {
  color: #CBD5E1;
}

.game-detail-dark .tab-text-content :deep(strong),
.game-detail-dark .tab-text-content :deep(b) {
  color: #FF8A4C;
}

.sponsor-link-box {
  background-color: #F8FAFC;
  padding: 10px 14px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 1rem;
}

.game-detail-dark .sponsor-link-box {
  background-color: #1E293B;
}

.sponsor-link-box a,
.sponsor-link-box i {
  color: #64748B;
  font-weight: 700;
  text-decoration: none;
}

.game-detail-dark .sponsor-link-box a,
.game-detail-dark .sponsor-link-box i {
  color: #FF8A4C;
}

.enter-btn {
  background: linear-gradient(135deg, #FF7A00 0%, #FF4D00 100%);
  color: #fff;
  font-size: 0.72rem;
  cursor: pointer;
}

.forum-open-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1.5px solid #CBD5E1;
  background: transparent;
  color: #64748B;
  border-radius: 999px;
  padding: 0.35rem 0.8rem;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  cursor: pointer;
}

.forum-open-btn:hover {
  color: #FF8A4C;
  border-color: #FF8A4C;
}

.game-detail-dark .forum-open-btn {
  border-color: #475569;
  color: #94A3B8;
}

.game-detail-dark .forum-open-btn:hover {
  color: #FF8A4C;
  border-color: #FF8A4C;
}

.badge-joined {
  background: rgba(5, 150, 105, 0.14);
  color: #059669;
  font-size: 0.72rem;
}

.game-detail-dark .badge-joined {
  background: rgba(52, 211, 153, 0.16);
  color: #34D399;
}
</style>
